/**
 * Shared utilities for DataTable column management.
 */

export interface ColumnField {
  fieldName: string;
  visibleByDefault?: boolean;
}

/**
 * URL parameter name for column selection
 */
export const columnsUrlParamName = "columns";

/**
 * Validate column names against available columns
 */
export const validateColumns = (
  columnNames: string[],
  allColumns: ColumnField[],
): string[] => {
  // Filter out invalid columns while preserving order
  return columnNames.filter((name) =>
    allColumns.some((col) => col.fieldName === name),
  );
};

/**
 * Get all available columns that aren't in the visibleColumns array
 * Used for showing unselected columns in the column menu
 */
export const getHiddenColumns = (
  visibleColumns: string[],
  allColumns: ColumnField[],
): string[] => {
  return allColumns
    .map((col) => col.fieldName)
    .filter((name) => !visibleColumns.includes(name));
};

/**
 * Move a column to a new position in the array
 */
export const moveColumn = (
  columns: string[],
  fromIndex: number,
  toIndex: number,
): string[] => {
  if (fromIndex === toIndex) return columns;

  const result = [...columns];
  const [removed] = result.splice(fromIndex, 1);
  result.splice(toIndex, 0, removed);

  return result;
};

/**
 * Get default visible columns.
 * If any columns have visibleByDefault set, only those columns are shown.
 * Otherwise, all columns are shown.
 */
export const getDefaultVisibleColumns = (
  allColumns: ColumnField[],
): string[] => {
  const columnsWithDefault = allColumns.filter(
    (col) => col.visibleByDefault === true,
  );

  if (columnsWithDefault.length > 0) {
    return columnsWithDefault.map((col) => col.fieldName);
  }

  return allColumns.map((col) => col.fieldName);
};

/**
 * Generate storage key for cached default columns
 */
export const getStorageKey = (tableId: string): string =>
  `dataTable_defaultColumns_${tableId}`;

/**
 * Load cached default columns from localStorage with validation, or the default if nothing is cached.
 */
export const loadCachedColumns = (
  tableId: string,
  allColumns: ColumnField[],
): string[] => {
  try {
    const cached = localStorage.getItem(getStorageKey(tableId));
    if (!cached) return getDefaultVisibleColumns(allColumns);

    const cachedColumns = JSON.parse(cached);
    if (!Array.isArray(cachedColumns)) {
      return getDefaultVisibleColumns(allColumns);
    }

    const validCachedColumns = validateColumns(cachedColumns, allColumns);

    return validCachedColumns.length > 0
      ? validCachedColumns
      : getDefaultVisibleColumns(allColumns);
  } catch (error) {
    console.warn("Failed to get default columns from cache:", error);
    return getDefaultVisibleColumns(allColumns);
  }
};

/**
 * Save default columns to localStorage
 */
export const saveCachedColumns = (
  tableId: string,
  columns: string[],
): boolean => {
  try {
    localStorage.setItem(getStorageKey(tableId), JSON.stringify(columns));
    return true;
  } catch (error) {
    console.warn("Failed to save default columns to cache:", error);
    return false;
  }
};

/**
 * Get default columns with fallback logic
 */
export const getDefaultColumns = (
  tableId: string | undefined,
  allColumns: ColumnField[],
): string[] => {
  if (!tableId) {
    return allColumns.map((col) => col.fieldName);
  }
  return loadCachedColumns(tableId, allColumns);
};

/**
 * Generate storage key for column sizes
 */
const getColumnSizesStorageKey = (tableId: string): string => {
  return `dataTable_columnSizes_${tableId}`;
};

/**
 * Load stored column sizes from localStorage
 */
export const loadStoredColumnSizes = (
  tableId: string,
): Record<string, number> => {
  if (!tableId) return {};
  try {
    const stored = localStorage.getItem(getColumnSizesStorageKey(tableId));
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.warn("Failed to load stored column sizes:", error);
    return {};
  }
};

/**
 * Save column sizes to localStorage
 */
export const saveColumnSizes = (
  tableId: string,
  columnSizing: Record<string, number>,
): void => {
  if (!tableId) return;
  try {
    localStorage.setItem(
      getColumnSizesStorageKey(tableId),
      JSON.stringify(columnSizing),
    );
  } catch (error) {
    console.warn("Failed to save column sizes:", error);
  }
};
